import type { CSSProperties, ReactNode } from "react";

import { classNames } from "helpers/commonHelpers";

import "./PackingListTable.scss";

export type PackingListColumn = {
  key: string;
  header: ReactNode;
  width?: number | string;
  required?: boolean;
  className?: string;
};

const buildDataColumnTemplate = (columns: PackingListColumn[]) =>
  columns
    .map((column) => {
      if (column.width == null) {
        return "minmax(120px, 1fr)";
      }

      return typeof column.width === "number" ? `${column.width}px` : column.width;
    })
    .join(" ");

// Fixed-width trailing gutter that reserves dedicated space for the row-actions
// (copy / delete) icons at the right end of every row, so they sit in their own
// lane and never overlap the last data cell (e.g. the commodity currency select).
const ROW_ACTIONS_GUTTER = "64px";

// Thin leading/trailing tracks holding the dummy sticky edge cells. Pinned to the
// viewport edges, their 1px lines act as the table's persistent left/right
// borders that never scroll away (see &__edge in PackingListTable.scss).
const EDGE = "1px";

const buildGridTemplate = (columns: PackingListColumn[]) => {
  const dataColumns = buildDataColumnTemplate(columns);
  return `${EDGE} 56px ${dataColumns} ${ROW_ACTIONS_GUTTER} ${EDGE}`;
};

type PackingListTableProps = {
  columns: PackingListColumn[];
  children: ReactNode;
  scrollable?: boolean;
  className?: string;
  testId?: string;
};

export default function PackingListTable({
  columns,
  children,
  scrollable = false,
  className,
  testId,
}: PackingListTableProps) {
  const tableStyle = scrollable ? ({ "--rfv2-pl-grid-columns": buildGridTemplate(columns) } as CSSProperties) : undefined;

  const table = (
    <div
      className={classNames("rfv2-packing-list-table", className ?? "", {
        "rfv2-packing-list-table--scrollable": scrollable,
      })}
      style={tableStyle}
      data-testid={scrollable ? undefined : testId}
    >
      <div className="rfv2-packing-list-table__header">
        <div className="rfv2-packing-list-table__edge rfv2-packing-list-table__edge--left" aria-hidden="true" />
        <div className="rfv2-packing-list-table__header-cell rfv2-packing-list-table__header-cell--number">No.</div>
        <div className="rfv2-packing-list-table__header-scroll">
          {columns.map((column) => (
            <div
              key={column.key}
              className={classNames("rfv2-packing-list-table__header-cell", column.className ?? "")}
              style={scrollable ? undefined : { width: column.width, minWidth: column.width }}
            >
              <span>{column.header}</span>
              {column.required && <span className="rfv2-packing-list-table__required">*</span>}
            </div>
          ))}
        </div>
        <div className="rfv2-packing-list-table__header-actions">...</div>
        <div className="rfv2-packing-list-table__edge rfv2-packing-list-table__edge--right" aria-hidden="true" />
      </div>
      <div className="rfv2-packing-list-table__body">{children}</div>
    </div>
  );

  if (!scrollable) {
    return table;
  }

  return (
    <div className="rfv2-packing-list-table-scroll" data-testid={testId}>
      {table}
    </div>
  );
}
